/**
 * The event model every generator in this package reads.
 *
 * Time is stored the way a user types it: calendar-date strings and wall-clock
 * date-times, with an optional IANA zone name alongside. Nothing here converts
 * between zones — there is no tz database and no `Date` arithmetic, so the same
 * draft renders identically on a server, in a browser, and under Hermes.
 */

/** An all-day date: `YYYY-MM-DD`. */
export interface EventDate {
  readonly kind: 'date';
  readonly date: string;
}

/**
 * A wall-clock time: `YYYY-MM-DDTHH:mm` or `YYYY-MM-DDTHH:mm:ss`.
 *
 * `timeZone` is an IANA name (`America/New_York`) or `UTC`. Omitted means
 * floating time — whatever zone the reading client happens to be in.
 */
export interface EventDateTime {
  readonly kind: 'dateTime';
  readonly dateTime: string;
  readonly timeZone?: string;
}

export type EventTime = EventDate | EventDateTime;

export interface EventPerson {
  readonly name?: string;
  readonly email: string;
}

export type EventStatus = 'confirmed' | 'tentative' | 'cancelled';

/**
 * Whether the time counts against the person's availability — iCalendar's `TRANSP`, and the
 * "Show As" row in Apple Calendar and Outlook. A lunch you're happy to be interrupted during is
 * `free`; the default for anything else is `busy`, which is also the format's default.
 */
export type EventShowAs = 'busy' | 'free';

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface EventRecurrence {
  readonly frequency: RecurrenceFrequency;
  /** Repeat every N periods; defaults to 1. */
  readonly interval?: number;
  /** Stop after N occurrences. Mutually exclusive with `until`. */
  readonly count?: number;
  /** Stop on this date (`YYYY-MM-DD`), inclusive. Mutually exclusive with `count`. */
  readonly until?: string;
}

export interface EventDraft {
  readonly title: string;
  readonly start: EventTime;
  /** For all-day events this is the last day, inclusive; omitted means a single day. */
  readonly end?: EventTime;
  readonly description?: string;
  readonly location?: string;
  readonly url?: string;
  readonly organizer?: EventPerson;
  readonly attendees?: readonly EventPerson[];
  /** Alarms, in minutes before the start. */
  readonly remindersMinutesBefore?: readonly number[];
  readonly recurrence?: EventRecurrence;
  readonly status?: EventStatus;
  /** Busy or free during the event. Omitted means busy, which is iCalendar's own default. */
  readonly showAs?: EventShowAs;
  /** Stable identity across regenerations; generators require it (see `toIcs`). */
  readonly uid?: string;
}

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

export function eventDate(date: string): EventDate {
  return { kind: 'date', date };
}

export function eventDateTime(dateTime: string, timeZone?: string): EventDateTime {
  return timeZone === undefined
    ? { kind: 'dateTime', dateTime }
    : { kind: 'dateTime', dateTime, timeZone };
}

export function isAllDay(draft: EventDraft): boolean {
  return draft.start.kind === 'date';
}

/** The calendar day a time falls on, for both all-day and wall-clock values. */
export function timeDate(time: EventTime): string {
  return time.kind === 'date' ? time.date : time.dateTime.slice(0, 10);
}

/** `HH:mm:ss` for a wall-clock time, `null` for an all-day date. */
export function timeClock(time: EventTime): string | null {
  if (time.kind === 'date') return null;
  const clock = time.dateTime.slice(11);
  return clock.length === 5 ? `${clock}:00` : clock;
}

/** The zone a draft is expressed in, if it names one. */
export function draftTimeZone(draft: EventDraft): string | undefined {
  return draft.start.kind === 'dateTime' ? draft.start.timeZone : undefined;
}

/**
 * The end a generator should use: the explicit end, or a same-instant fallback
 * (the start) so callers never have to invent one.
 */
export function effectiveEnd(draft: EventDraft): EventTime {
  return draft.end ?? draft.start;
}
